import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { HsButtonModule, HsTabGroupModule } from '@hsetu/ngui-components';
import { ContactType, HsContact, HsContactModule } from '@hsetu/ngui-views';
import { ContactService } from '@modules/contact/services/contact.service';
import { BankAccount } from '@modules/contact/models/bank-account.model';
import { ContactMapper } from '@modules/contact/mappers/contact.mapper';
import { Registration } from '@modules/contact/models/registration.model';
import { KfmContactBankAccountFormComponent } from '../contact-bank-account-form/kfm-contact-bank-account-form.component';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { KfmContactRegistrationsComponent } from '../contact-registrations/kfm-contact-registrations.component';
import { Contact } from '@modules/contact/models/contact.model';
import { KfmContactLogoComponent } from '../contact-logo/kfm-contact-logo.component';
import {
  ADDRESS_TAB_LABEL,
  BANK_TAB_LABEL,
  CANCEL_LABEL,
  ID_TAB_LABEL,
  LOGO_TAB_LABEL,
  SAVE_LABEL,
} from '@modules/contact/i18n/label-text-constants';

@Component({
  selector: 'kfm-profile-dialog',
  standalone: true,
  imports: [
    CommonModule,
    HsTabGroupModule,
    HsContactModule,
    HsButtonModule,
    KfmContactBankAccountFormComponent,
    KfmContactRegistrationsComponent,
    KfmContactLogoComponent,
  ],
  templateUrl: './kfm-contact-profile-dialog.component.html',
  host: { class: 'kfm-profile-dialog layer-one' },
})
export class KfmContactProfileDialogComponent implements OnInit {
  private readonly _contactService = inject(ContactService);
  private readonly _guidGenerator = inject(GuidGeneratorService);
  private readonly _mapper = new ContactMapper();

  protected readonly dialogRef = inject<DialogRef<void>>(DialogRef<void>);

  protected readonly addressTabLabel = ADDRESS_TAB_LABEL;
  protected readonly logoTabLabel = LOGO_TAB_LABEL;
  protected readonly bankTabLabel = BANK_TAB_LABEL;
  protected readonly idTabLabel = ID_TAB_LABEL;
  protected readonly okButtonLabel = SAVE_LABEL;
  protected readonly cancelButtonLabel = CANCEL_LABEL;

  private originalContact?: Contact = undefined;

  protected readonly contact = signal<HsContact>(null!);
  protected readonly bankAccount = signal<BankAccount | undefined>(undefined);
  protected readonly registrations = signal<Registration[]>([]);
  protected readonly contactType = computed(() => {
    const contact = this.contact();
    return contact ? contact.contactType : ContactType.None;
  });

  public async ngOnInit() {
    try {
      this.originalContact = await this._contactService.getTenantContact();
    } catch (error) {
      console.error(
        '[ContactProfileDialog] Fehler beim Laden des Mandantenkontakts:',
        error,
      );
      this.originalContact = undefined;
    }

    if (this.originalContact) {
      this.contact.set(this._mapper.mapToHsContact(this.originalContact));

      // Ensure bank account is set
      if (
        this.originalContact.bankAccounts &&
        this.originalContact.bankAccounts.length > 0
      ) {
        this.bankAccount.set(this.originalContact.bankAccounts[0]);
      } else {
        this.bankAccount.set({
          uId: this._guidGenerator.emptyUUID,
          tenantUId: this._guidGenerator.emptyUUID,
          userUId: this._guidGenerator.emptyUUID,
          stampCreate: new Date().toISOString(),
          syncType: 0,
          concurrencyIdentificator: this._guidGenerator.emptyUUID,
          concurrencyStamp: new Date().toISOString(),
          contactUId: this.originalContact ? this.originalContact.uId : '',
        });
      }

      if (
        this.originalContact.registrations &&
        this.originalContact.registrations.length > 0
      ) {
        this.registrations.set(this.originalContact.registrations);
      }
    }
  }

  protected async onOkClicked(): Promise<void> {
    const contact = this.contact();

    if (this.originalContact && contact) {
      const registrations = this.registrations();

      for (const reg of registrations) {
        if (
          !reg.contactUId ||
          reg.contactUId === '' ||
          reg.contactUId === this._guidGenerator.emptyUUID
        ) {
          reg.contactUId = this.originalContact.uId;
        }
      }

      this.originalContact.registrations = registrations;

      this._mapper.mapHsContactToContact(contact, this.originalContact);

      this.originalContact.bankAccounts = (
        this.originalContact.bankAccounts ?? []
      ).filter((ba) => ba.uId !== this._guidGenerator.emptyUUID);
      this.originalContact.bankAccounts.push(this.bankAccount());

      await this._contactService.updateTenantContact(this.originalContact);
    }

    this.dialogRef.close();
  }
}
